import {
  BitkitAlert,
  BitkitButton,
  BitkitCheckbox,
  BitkitCodeSnippet,
  BitkitLightbox,
  BitkitLink,
  BitkitList,
  BitkitSteps,
  createBitkitToast,
  IconArrowLeft,
  IconArrowNortheast,
  IconArrowRight,
  rem,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { AspectRatio } from '@chakra-ui/react/aspect-ratio';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Group } from '@chakra-ui/react/group';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { useCallback, useContext, useMemo, useState } from 'react';

import { useBuildCacheAddonTrial } from '@/client/BFF/Organizations/AddonTrials/useBuildCacheAddonTrial';
import {
  BuildCacheTrialSource,
  useBuildCacheStartTrial,
} from '@/client/BFF/Organizations/AddonTrials/useBuildCacheStartTrial';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import VideoLinkBox from '@/components/VideoLinkBox/VideoLinkBox';
import URLs from '@/enums/urls';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { PublicApiBuild } from '@/models/Build';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import WarmUpCacheStep from '@/pages/BuildCache/Connections/WarmUpCacheStep';
import useBuildDetailsPageStore from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { buildCacheInvocationListPath, workflowEditorPath } from '@/utils/monolithApiRouteService';

type SetupStepType = 'connectWorkflow' | 'warmUpCache';
const SETUP_STEPS_DATA = {
  connectWorkflow: {
    label: 'Workflow',
  },
  warmUpCache: {
    label: 'Cache warming',
  },
};
const SETUP_STEPS_DATA_KEYS = Object.keys(SETUP_STEPS_DATA) as SetupStepType[];

type SetUpBuildCacheProps = {
  build: PublicApiBuild;
};

const SetUpBuildCache = ({ build }: SetUpBuildCacheProps) => {
  const { isMobile } = useResponsive();
  const { open: isVideoOpen, onClose: closeVideo, onOpen: openVideo } = useDisclosure();

  const app = useBuildDetailsPageStore((state) => state.app);
  const abilities = useBuildDetailsPageStore((state) => state.abilities);

  const {
    account: { slug: workspaceSlug, hasBuildCacheSubscription, canActivateBuildCacheTrial },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { trial: buildCacheTrial, isLoading: isBuildCacheTrialLoading } = useBuildCacheAddonTrial(workspaceSlug);
  const cannotStartAddNewConnection = !canActivateBuildCacheTrial && !buildCacheTrial && !hasBuildCacheSubscription;

  const buildCacheOverViewPagePath = buildCacheInvocationListPath(workspaceSlug, {
    params: { tool: BuildCacheToolIds.gradle, project_slug: app.slug, workflow: build.triggeredWorkflow },
  });
  // Optimistic in-session flag for a just-started trial; the real state comes from the async query.
  const [trialStartedInSession, setTrialStartedInSession] = useState(false);
  const hasBuildCacheTrial = !!buildCacheTrial || trialStartedInSession;
  const [configureWorkflowStepCompleted, setConfigureWorkflowStepCompleted] = useState(false);
  const [startTrialConfirmed, setStartTrialConfirmed] = useState(false);
  const [warmUpCacheStepCompleted, setWarmUpCacheStepCompleted] = useState(false);

  const [currentStep, setCurrentStep] = useState<SetupStepType>(SETUP_STEPS_DATA_KEYS[0]);
  const indexOfCurrentStep = useMemo(() => {
    return SETUP_STEPS_DATA_KEYS.indexOf(currentStep);
  }, [currentStep]);

  const canStartTrial = !isBuildCacheTrialLoading && !hasBuildCacheTrial && !cannotStartAddNewConnection;

  const { startTrial } = useBuildCacheStartTrial(workspaceSlug, {
    onSuccess: () => {
      createBitkitToast({
        'data-clarity-unmask': 'true',
        dismissible: true,
        messageText: 'Your 30-day trial has been activated.',
        variant: 'info',
      });
    },
    onError: () => {
      createBitkitToast({
        'data-clarity-unmask': 'true',
        dismissible: true,
        messageText: (
          <>
            Please{' '}
            <BitkitLink href={URLs.CONTACT_SALES} isExternal textDecoration="underline">
              contact us
            </BitkitLink>{' '}
            for assistance.
          </>
        ),
        titleText: "We couldn't activate your trial",
        variant: 'critical',
      });
      setTrialStartedInSession(false);
      setStartTrialConfirmed(false);
    },
  });

  const trackStepCompletedEvent = useCallback(() => {
    let eventName;
    const eventProps = {
      app_slug: app.slug,
      build_cache_tool: BuildCacheToolIds.gradle,
      build_slug: build.slug,
      project_id: app.projectId ?? 'unsetProjectId',
      project_title: app.title,
      has_build_cache_trial: hasBuildCacheTrial,
      has_build_cache_subscription: hasBuildCacheSubscription,
      is_start_caching_trial_option_available: !buildCacheTrial && cannotStartAddNewConnection,
      is_start_caching_trial_checked: startTrialConfirmed,
    };

    switch (indexOfCurrentStep) {
      case 0:
        eventName = 'Set Up Build Cache Connection Configure Workflow Step Completed';
        break;
      case 1:
        eventName = 'Set Up Build Cache Connection Cache Warmup Step Completed';
        break;
      default:
        return;
    }

    segmentTrack(eventName, eventProps);
  }, [
    app,
    build,
    buildCacheTrial,
    cannotStartAddNewConnection,
    hasBuildCacheSubscription,
    hasBuildCacheTrial,
    indexOfCurrentStep,
    startTrialConfirmed,
  ]);

  const goToAPreviousStep = useCallback(() => {
    const previousStep = SETUP_STEPS_DATA_KEYS[indexOfCurrentStep - 1];

    if (indexOfCurrentStep === 1) {
      setWarmUpCacheStepCompleted(false);
      setStartTrialConfirmed(false);
      setConfigureWorkflowStepCompleted(false);
    }

    setCurrentStep(previousStep);
  }, [indexOfCurrentStep]);

  const onStepCompleted = useCallback(() => {
    trackStepCompletedEvent();

    if (indexOfCurrentStep === 0 && configureWorkflowStepCompleted) {
      if (canStartTrial) {
        startTrial(BuildCacheTrialSource.buildDetailsPageBuildCacheTab);
        setTrialStartedInSession(true);
      }
    }

    if (indexOfCurrentStep === 1 && warmUpCacheStepCompleted) {
      window.open(buildCacheOverViewPagePath, '_blank');
      setWarmUpCacheStepCompleted(true);
      return;
    }

    setCurrentStep(SETUP_STEPS_DATA_KEYS[indexOfCurrentStep + 1]);
  }, [
    trackStepCompletedEvent,
    indexOfCurrentStep,
    configureWorkflowStepCompleted,
    warmUpCacheStepCompleted,
    canStartTrial,
    startTrial,
    buildCacheOverViewPagePath,
  ]);

  const isNextEnabled =
    (indexOfCurrentStep === 0 &&
      configureWorkflowStepCompleted &&
      (hasBuildCacheTrial || cannotStartAddNewConnection ? true : startTrialConfirmed)) ||
    (indexOfCurrentStep === 1 && warmUpCacheStepCompleted);

  return (
    <Box display="flex" flexDirection="column" gap="24">
      <Box display="flex" flexDirection="column" gap="4">
        <Text textStyle="heading/h3" color="text/primary">
          Set up Build Cache
        </Text>
        <Text textStyle="body/md/regular" color="text/secondary">
          To optimize build duration of this Workflow, you can complete a 2-step process either with an active trial or
          after subscribing to Build Cache.
        </Text>
      </Box>
      <Card.Root display="flex" flexDirection="column" maxWidth="64rem" height="fit-content" width="100%">
        <Box paddingBlock="32" display={['none', 'flex']}>
          <BitkitSteps orientation={isMobile ? 'vertical' : 'horizontal'} step={indexOfCurrentStep}>
            {SETUP_STEPS_DATA_KEYS.map((stepID, index) => (
              <BitkitSteps.Item
                key={stepID}
                label={SETUP_STEPS_DATA[stepID].label}
                state={index > indexOfCurrentStep ? 'disabled' : undefined}
              />
            ))}
          </BitkitSteps>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          padding="32"
          paddingBlockEnd="48"
          gap="24"
          borderBlockStart="1px solid"
          borderColor="border/regular"
        >
          {indexOfCurrentStep === 0 && (
            <>
              <Box display="flex" flexDirection="column" gap="24">
                <Box display="flex" flexDirection="column" gap="4">
                  <Text textStyle="heading/h3" color="text/primary">
                    Configure the Workflow
                  </Text>
                  <Text textStyle="body/md/regular" color="text/body">
                    To speed up builds with Build Cache, a Bitrise Step needs to be added.
                  </Text>
                </Box>
                <Box display="flex" flexDirection={['column', 'row']} gap="48">
                  <Box flexShrink="0" maxWidth="38.75rem">
                    <BitkitList variant="explainer">
                      <BitkitList.Item>
                        <Text as="span">
                          Select the{' '}
                          <Text as="span" textStyle="body/lg/semibold" color="text/body">
                            {build.triggeredWorkflow}
                          </Text>{' '}
                          Workflow in the Editor.{' '}
                          {abilities.canChangeSettings && (
                            <BitkitLink
                              colorVariant="purple"
                              href={workflowEditorPath(app.slug, build.triggeredWorkflow)}
                              isExternal
                              suffixIcon={IconArrowNortheast}
                              whiteSpace="nowrap"
                            >
                              Go to Workflow Editor
                            </BitkitLink>
                          )}
                        </Text>
                      </BitkitList.Item>
                      <BitkitList.Item>
                        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4">
                          <Text as="span">
                            Add the{' '}
                            <BitkitCodeSnippet variant="inline" size="md" interactive>
                              activate-build-cache-for-gradle
                            </BitkitCodeSnippet>{' '}
                            Step to your Workflow before the Step you want to speed up.
                          </Text>
                          <BitkitLink
                            colorVariant="purple"
                            href="https://docs.bitrise.io/en/bitrise-build-cache/build-cache-for-gradle/configuring-the-build-cache-for-gradle-in-the-bitrise-ci-environment.html"
                            isExternal
                          >
                            Learn more
                          </BitkitLink>
                        </Box>
                      </BitkitList.Item>
                      <BitkitList.Item
                        helperText={
                          app.usesRepositoryYml
                            ? 'Your configuration YAML is stored in your Git repository, you should commit the changes.'
                            : undefined
                        }
                      >
                        Save changes
                      </BitkitList.Item>
                    </BitkitList>
                  </Box>
                  <VideoLinkBox
                    display="flex"
                    justifyContent="center"
                    onClick={() => {
                      openVideo();
                    }}
                    label="View tutorial"
                    width="100%"
                    height={rem(140)}
                    marginBottom="8"
                  />
                </Box>
                <Separator />
                <Box display="flex" flexDirection="column" gap="16">
                  <Text textStyle="body/lg/regular" color="text/body">
                    {`To proceed, confirm that you have completed configuring your Workflow${hasBuildCacheTrial ? '' : 'and that you will start the 30-day trial'}.`}
                  </Text>
                  <BitkitCheckbox
                    labelText="I completed the above configuration steps."
                    onCheckedChange={({ checked }) => setConfigureWorkflowStepCompleted(checked === true)}
                  />
                  {canStartTrial && (
                    <>
                      <BitkitCheckbox
                        labelText="I confirm starting the 30-day free trial."
                        onCheckedChange={({ checked }) => setStartTrialConfirmed(checked === true)}
                      />
                      <BitkitAlert
                        variant="info"
                        titleText="This will start your 30-day free trial"
                        messageText={
                          <>
                            We won’t charge you when your trial ends.{' '}
                            <BitkitLink href={URLs.PRICING_BUILD_CACHE} textDecoration="underline" isExternal>
                              Learn more about our pricing
                            </BitkitLink>
                          </>
                        }
                      />
                    </>
                  )}
                </Box>
              </Box>
              <BitkitLightbox
                aria-label="Build Cache setup on Bitrise CI"
                onOpenChange={({ open }) => {
                  if (!open) {
                    closeVideo();
                  }
                }}
                open={isVideoOpen}
                size="lg"
              >
                <AspectRatio ratio={16 / 10}>
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/h3LXuFSVrI8"
                    frameBorder="0"
                    title="Build Cache setup on Bitrise CI"
                    allowFullScreen
                  />
                </AspectRatio>
              </BitkitLightbox>
            </>
          )}
          {indexOfCurrentStep === 1 && (
            <WarmUpCacheStep
              onWarmUpCacheCompleted={setWarmUpCacheStepCompleted}
              buildCacheOverViewPagePath={buildCacheOverViewPagePath}
            />
          )}
        </Box>
        <Group
          alignItems="stretch"
          borderBlockStart="1px solid"
          borderColor="border/regular"
          flexDirection={['column', 'row']}
          gap={['16', '32']}
          justifyContent="flex-end"
          marginBlockStart="auto"
          padding="32"
        >
          {indexOfCurrentStep >= 1 && (
            <BitkitButton onClick={goToAPreviousStep} variant="secondary" icon={IconArrowLeft}>
              Previous
            </BitkitButton>
          )}
          <BitkitButton
            onClick={onStepCompleted}
            state={!isNextEnabled ? 'disabled' : undefined}
            suffixIcon={indexOfCurrentStep === 1 ? IconArrowNortheast : IconArrowRight}
            variant="secondary"
          >
            {indexOfCurrentStep === 1 ? 'View in Build Cache' : 'Next'}
          </BitkitButton>
        </Group>
      </Card.Root>
    </Box>
  );
};

export default SetUpBuildCache;
